import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"

import { HolderOutlined } from "@ant-design/icons"
import { Modal, Spin, Typography } from "antd"
import { DndProvider, useDrag, useDragLayer, useDrop } from "react-dnd"
import { getEmptyImage, HTML5Backend } from "react-dnd-html5-backend"
import { useTranslation } from "react-i18next"

import Button from "../../../../Components/Button"

import styles from "./ManageOrderModal.module.less"

const { Text } = Typography

const ITEM_TYPE = "PerkOrderItem"
const PREVIEW_CELLS = 6

const formatWebsite = (url) => (url ? url.replace(/^https?:\/\//, "") : "")

const ItemCard = ({ index, perk, className }) => {
  const { t } = useTranslation()
  const isActive = !!perk.is_visible

  return (
    <div className={`${styles.item} ${className || ""}`}>
      <span className={styles.index}>{index + 1}</span>
      <div className={styles.info}>
        <span className={styles.name}>{perk.partner_name}</span>
        <span className={styles.website}>{formatWebsite(perk.partner_website)}</span>
      </div>
      <span className={`${styles.badge} ${!isActive ? styles.badgeInactive : ""}`}>
        {isActive ? t("Active") : t("Inactive")}
      </span>
      <span className={styles.handle}>
        <HolderOutlined />
      </span>
    </div>
  )
}

const OrderItem = ({ index, perk, moveItem }) => {
  const ref = useRef()

  const [, drop] = useDrop({
    accept: ITEM_TYPE,
    hover: (item, monitor) => {
      if (!ref.current) return

      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return

      // Swap only when the cursor has crossed the middle of the card —
      // otherwise, during the FLIP animation, the hover is triggered again and the list "twitches"
      const rect = ref.current.getBoundingClientRect()
      const middleY = (rect.bottom - rect.top) / 2
      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) return
      const hoverClientY = clientOffset.y - rect.top

      if (dragIndex < hoverIndex && hoverClientY < middleY) return
      if (dragIndex > hoverIndex && hoverClientY > middleY) return

      moveItem(dragIndex, hoverIndex)
      item.index = hoverIndex
    }
  })

  const [{ isDragging }, drag, preview] = useDrag({
    type: ITEM_TYPE,
    item: () => ({
      index,
      id: perk.id,
      width: ref.current?.getBoundingClientRect().width
    }),
    collect: (m) => ({ isDragging: m.isDragging() })
  })

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true })
  }, [preview])

  drag(drop(ref))

  return (
    <div ref={ref} data-flip-id={perk.id} data-testid={`manage-order-item-${perk.id}`}>
      <ItemCard index={index} perk={perk} className={isDragging ? styles.itemPlaceholder : ""} />
    </div>
  )
}

const DragPreviewLayer = ({ items }) => {
  const { isDragging, item, currentOffset, initialOffset, initialSourceOffset, width } =
    useDragLayer((monitor) => ({
      isDragging: monitor.isDragging(),
      item: monitor.getItem(),
      currentOffset: monitor.getClientOffset(),
      initialOffset: monitor.getInitialClientOffset(),
      initialSourceOffset: monitor.getInitialSourceClientOffset(),
      width: monitor.getItem()?.width
    }))

  if (!isDragging || !currentOffset || !initialOffset || !initialSourceOffset) {
    return null
  }

  const perk = items.find((p) => p.id === item.id)
  if (!perk) return null

  const offsetX = initialOffset.x - initialSourceOffset.x
  const offsetY = initialOffset.y - initialSourceOffset.y

  const style = {
    position: "fixed",
    pointerEvents: "none",
    left: 0,
    top: 0,
    transform: `translate(${currentOffset.x - offsetX}px, ${currentOffset.y - offsetY}px)`,
    width: width || 480,
    zIndex: 1100
  }

  return (
    <div style={style}>
      <ItemCard index={item.index} perk={perk} className={styles.itemGhost} />
    </div>
  )
}

const ManageOrderModal = ({ open, items, loading, submitting, onSubmit, onCancel }) => {
  const { t } = useTranslation()
  const [order, setOrder] = useState([])
  const listRef = useRef(null)
  const flipPositions = useRef(new Map())

  useEffect(() => {
    if (open) {
      flipPositions.current.clear()
      setOrder(items || [])
    }
  }, [open, items])

  // FLIP: after reordering, each card instantly "returns" to the old one
  // place via transform and then smoothly animates to a new position
  useLayoutEffect(() => {
    const list = listRef.current
    if (!list) return

    list.querySelectorAll("[data-flip-id]").forEach((node) => {
      const id = node.dataset.flipId
      const prevTop = flipPositions.current.get(id)
      const newTop = node.offsetTop
      flipPositions.current.set(id, newTop)

      if (prevTop === undefined || prevTop === newTop) return

      node.style.transition = "none"
      node.style.transform = `translateY(${prevTop - newTop}px)`
      node.getBoundingClientRect() // force reflow
      node.style.transition = "transform 200ms ease"
      node.style.transform = ""
    })
  }, [order])

  const moveItem = (from, to) => {
    setOrder((prev) => {
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next
    })
  }

  const handleSave = () => {
    onSubmit({
      perks: order.map((p, i) => ({ id: p.id, display_order: i + 1 }))
    })
  }

  const previewCells = useMemo(() => Array.from({ length: PREVIEW_CELLS }, (_, i) => i + 1), [])

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={1000}
      closable={false}
      title={null}
      destroyOnClose
      maskClosable={false}
      className={styles.modal}
      getContainer={() => document.getElementById("main-layout")}
      data-testid="manage-order-modal"
    >
      <DndProvider backend={HTML5Backend}>
        <div className={styles.layout}>
          <div className={styles.left}>
            <div className={styles.leftHeader}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {t("Manage order")}
              </Typography.Title>
              <button className={styles.closeBtn} onClick={onCancel} aria-label="Close">
                ✕
              </button>
            </div>
            <Text className={styles.description}>
              {t(
                "Drag and drop partner cards to change the order displayed on the User-side Perks & Benefits page."
              )}
            </Text>

            <Spin spinning={loading}>
              <div className={styles.list} ref={listRef}>
                {order.map((perk, index) => (
                  <OrderItem key={perk.id} index={index} perk={perk} moveItem={moveItem} />
                ))}
                {!loading && order.length === 0 && (
                  <div className={styles.empty}>{t("No active partners")}</div>
                )}
              </div>
            </Spin>

            <div className={styles.footer}>
              <Button
                onClick={onCancel}
                disabled={submitting}
                data-testid="manage-order-cancel-btn"
              >
                {t("Cancel")}
              </Button>
              <Button
                type="primary"
                loading={submitting}
                disabled={order.length === 0}
                onClick={handleSave}
                data-testid="manage-order-save-btn"
              >
                {t("Save")}
              </Button>
            </div>
          </div>

          <div className={styles.right}>
            <Typography.Title level={4} className={styles.rightTitle}>
              {t("User platform perks and benefits placement")}
            </Typography.Title>

            <Text className={styles.rightDescription}>
              {t("Cards will be displayed on the User-side Perks & Benefits page in this order.")}
            </Text>
            <div className={styles.gridWrapper}>
              <div className={styles.grid}>
                {previewCells.map((n) => (
                  <div key={n} className={styles.cell}>
                    <span className={styles.cellNumber}>{n}</span>
                  </div>
                ))}
              </div>
              <div className={styles.gridMask} />
            </div>
          </div>
        </div>

        <DragPreviewLayer items={order} />
      </DndProvider>
    </Modal>
  )
}

export default ManageOrderModal
